import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import CookieBanner from "@/components/CookieBanner";
import SkipLink from "@/components/SkipLink";

// Cabecera pública compartida por Landing, Calculadora y Blog — misma barra
// fija en todas para que la navegación no "pierda" el menú al cambiar de página.
// Los anclajes (#features, #asesorias, #precios) apuntan a "/#..." para que
// funcionen tanto en la propia Landing (scroll) como desde otra página (vuelve
// a la Landing y salta a la sección).
export default function PublicHeader() {
  const { t } = useTranslation();
  // Solo gestiona el desplegable "Más" del nav condensado (768-1023px). Controlado
  // (open/onOpenChange) en vez de dejar a Radix el modo no controlado: "Funcionalidades"
  // es <a href="/#features"> (ancla, no <Link>), así que dentro de Landing pulsarlo no
  // desmonta el componente y el Popover no se cerraría solo (hallazgo de arquitecto.md,
  // sección 1) — de ahí el onClick explícito en los 3 enlaces de abajo.
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = () => setMoreOpen(false);
  return (
    <>
    <SkipLink />
    <header className="sticky top-0 z-50 border-b border-brand-edge/60 bg-card/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-4 sm:px-6">
        {/* shrink-0: el logo nunca cede ancho al algoritmo de flex-shrink (causa
            de la distorsión de proporción). Con eso, su ancho SIEMPRE se resuelve
            de forma proporcional al alto marcado por la clase de altura de abajo —
            nunca deformado. La altura escalona en pasos porque, con el bloque de
            controles ya recortado al máximo razonable (ver más abajo), el logo a
            tamaño completo (h-9) no cabe en todo el rango 360-390px sin volver a
            provocar overflow (ver arquitecto.md, "Preguntas abiertas", y entrega.md
            para el presupuesto de espacio medido tras quitar el icono del CTA). */}
        <Link to="/" className="flex shrink-0 items-center" data-testid="brand-logo" aria-label="Tu Aliado Hostelero - Inicio">
          <BrandLogo className="h-4 min-[375px]:h-5 min-[390px]:h-7 min-[414px]:h-9 w-auto" />
        </Link>
        {/* Nav completo (>=1024px, "lg" de fábrica de Tailwind): idéntico al de
            siempre, solo cambia el breakpoint de activación (antes "md", ahora
            "lg") para dejar hueco al nav condensado de abajo en 768-1023px. */}
        <nav className="hidden gap-8 lg:flex">
          <a href="/#features" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-features">{t("landing.nav.features")}</a>
          <a href="/#asesorias" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-asesorias">{t("landing.nav.consulting")}</a>
          <Link to="/calculadora" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-calculadora">{t("landing.nav.calculator")}</Link>
          <Link to="/blog" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-blog">{t("landing.nav.blog")}</Link>
          <a href="/#precios" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-pricing">{t("landing.nav.pricing")}</a>
        </nav>
        {/* Nav condensado (768-1023px, opción C ya elegida por Jesús): Precios y
            Asesorías siempre visibles como texto, resto detrás de "Más ▾". Testids
            propios en todo este bloque (nunca nav-features/nav-calculadora/nav-blog/
            nav-asesorias/nav-pricing) porque el nav completo de arriba sigue en el
            DOM, solo oculto por CSS, en este mismo rango — reusar esos testids
            duplicaría el data-testid en el documento (criterio 6). */}
        <nav className="hidden items-center gap-4 md:flex lg:hidden">
          <a href="/#precios" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-condensed-pricing">{t("landing.nav.pricing")}</a>
          <a href="/#asesorias" className="text-sm font-medium text-brand-stone hover:text-brand-ink" data-testid="nav-condensed-asesorias">{t("landing.nav.consulting")}</a>
          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-brand-stone hover:text-brand-ink"
                data-testid="nav-more-trigger"
              >
                {t("landing.nav.more")}
                <ChevronDown size={14} strokeWidth={2} className={cn("transition-transform", moreOpen && "rotate-180")} aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              sideOffset={14}
              className="w-auto min-w-[190px] border-brand-edge bg-card p-1.5"
              data-testid="nav-more-dropdown"
            >
              <a href="/#features" onClick={closeMore} className="block rounded-md px-3 py-2 text-sm font-medium text-brand-stone hover:bg-brand-cream hover:text-brand-ink" data-testid="nav-more-features">{t("landing.nav.features")}</a>
              <Link to="/calculadora" onClick={closeMore} className="block rounded-md px-3 py-2 text-sm font-medium text-brand-stone hover:bg-brand-cream hover:text-brand-ink" data-testid="nav-more-calculadora">{t("landing.nav.calculator")}</Link>
              <Link to="/blog" onClick={closeMore} className="block rounded-md px-3 py-2 text-sm font-medium text-brand-stone hover:bg-brand-cream hover:text-brand-ink" data-testid="nav-more-blog">{t("landing.nav.blog")}</Link>
              <Link to="/descargar-app" onClick={closeMore} className="block rounded-md px-3 py-2 text-sm font-medium text-brand-stone hover:bg-brand-cream hover:text-brand-ink" data-testid="nav-more-descargar-app">{t("landing.nav.downloadApp")}</Link>
            </PopoverContent>
          </Popover>
        </nav>
        <div className="flex items-center gap-0.5 sm:gap-3">
          {/* compact: prop opt-in (ver LanguageSwitcher.jsx/ThemeSwitcher.jsx), solo
              para este header público — AppLayout.jsx sigue sin pasar props y por
              tanto sin cambio de comportamiento (criterio 8). */}
          <LanguageSwitcher compact />
          <ThemeSwitcher compact />
          <Link to="/login" className="btn-ghost !py-2 !px-4 whitespace-nowrap" data-testid="header-login-link">
            <span className="sm:hidden">{t("landing.nav.loginMovil")}</span>
            <span className="hidden sm:inline">{t("landing.nav.login")}</span>
          </Link>
          <Link to="/registro" className="btn-primary !py-2 !px-4 whitespace-nowrap" data-testid="header-register-btn">
            {t("landing.nav.registerCta")} <ArrowRight size={16} className="hidden md:inline-block" />
          </Link>
        </div>
      </div>
    </header>
    <CookieBanner />
    </>
  );
}
